import axios from 'axios';
import type { RevenueRadar } from '../../services/revenueTypes';
import { API_URL } from '../../config/api';
import SessionService from '../../services/SessionService';

/** Feature flag: disable with REACT_APP_MENDS_VISION=0 */
export function isMendsVisionEnabled(): boolean {
  const v = (process.env.REACT_APP_MENDS_VISION || '1').trim().toLowerCase();
  return v !== '0' && v !== 'false' && v !== 'off';
}

function authHeaders() {
  const token = SessionService.getToken();
  return {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

/** Normalize API payloads (camelCase or PascalCase) into camelCase keys one level deep + nested arrays. */
function normalizeKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeKeys);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const camel = k.length ? k[0].toLowerCase() + k.slice(1) : k;
      out[camel] = normalizeKeys(v);
    }
    return out;
  }
  return value;
}

async function unwrap<T>(promise: Promise<{ data: { data?: T; Data?: T } }>): Promise<T> {
  const res = await promise;
  const raw = (res.data.data ?? res.data.Data) as T;
  return normalizeKeys(raw) as T;
}

export interface TodayItem {
  id: string;
  kind: string;
  title: string;
  detail: string;
  contactId?: number | null;
  contactName?: string | null;
  priority: string;
  actionLabel?: string | null;
  actionPath?: string | null;
  secondaryLabel?: string | null;
  secondaryPath?: string | null;
  at?: string | null;
  agentId?: number | null;
  agentName?: string | null;
  agentKind?: string | null;
  /** HJ-01 / Bloco 6 — fundamento estruturado (quando o today-v2 envia). */
  rule?: string | null;
  evidence?: string | null;
  pedido?: string | null;
  valor?: string | null;
  /** HJ-05 — fim da reunião. */
  endsAt?: string | null;
  /** HJ-05 — situação real ("Agendada", "Em andamento", "Terminou — registre o desfecho"). */
  status?: string | null;
  /** Quem: responsável pela reunião ou autor digital da ação (HJ-05/HJ-06). */
  actor?: string | null;
  /** Origem do item (Google Calendar, audit, crm_move, follow_up, meeting, scheduled…). */
  source?: string | null;
  /** ai_action de movimento do funil: coluna de destino. */
  columnId?: number | null;
  /** A IA executou sozinha (ninguém aprovou). */
  autoExecuted?: boolean | null;
  /** IB-05 — o prazo de atendimento venceu e a conversa foi escalada à equipe. */
  slaBreached?: boolean | null;
}

export interface RegisterScreenContextPayload {
  screen?: string;
  pathname?: string;
  contactId?: number;
  contactName?: string;
  conversationId?: number;
  messageId?: number;
  calendarEventId?: string;
  subjectKind?: 'qualified_contact';
  /** Card (oportunidade) aberto na tela: o Registrar vincula a mudança de etapa a ele (REG-04). */
  opportunityId?: number | string;
}

export interface TodayDto {
  greeting: string;
  subtitle: string;
  needsYou: TodayItem[];
  agenda: TodayItem[];
  followUp: { enabled: boolean; rulesCount: number; summary: string };
  approvalsPending: number;
}

export interface SearchHit {
  type: string;
  title: string;
  subtitle?: string | null;
  path: string;
  contactId?: number | null;
}

export interface SearchResult {
  hits: SearchHit[];
  aiAnswer?: string | null;
  usedLlm: boolean;
}

/** Tipo do rascunho no chat (valores do contrato do backend). */
export type MindChatDraftKind = 'proposal' | 'follow_up';

/** Plano do PDF da proposta (ProposalPdfPlan). missingFields são as chaves do modelo do setor. */
export interface MindChatDraftPdf {
  status: 'available' | 'missing_fields' | 'not_configured' | 'blocked';
  missingFields: string[];
  templateName?: string | null;
}

/** Rascunho devolvido pelo chat (MindChatDraftDto). text é nulo com awaiting = 'value' (falta o valor). */
export interface MindChatDraft {
  kind: MindChatDraftKind;
  contactId: number;
  contactName: string;
  text?: string | null;
  usedFacts: string[];
  sendRequested: boolean;
  pdf?: MindChatDraftPdf | null;
  awaiting?: 'value' | null;
}

/** Rascunho aberto que o front devolve ao chat para continuar a edição (MindChatOpenDraftDto). */
export interface MindChatOpenDraft {
  kind: MindChatDraftKind;
  contactId: number;
  contactName?: string;
  text?: string;
  awaiting?: 'value';
}

export interface MindChatAction {
  kind: string;
  title: string;
  subtitle?: string | null;
  path: string;
  contactId?: number | null;
  requiresConfirm: boolean;
  approvalTitle?: string | null;
  approvalBody?: string | null;
  suggestedAction?: string | null;
  actionKind?: string | null;
  actionPayload?: string | null;
  /** Presente quando kind === 'draft' (requiresConfirm false, path pode vir vazio). */
  draft?: MindChatDraft | null;
}

export interface MindChatResult {
  reply: string;
  usedLlm: boolean;
  source?: string | null;
  actions: MindChatAction[];
  /** Rota decidida pelo classificador da Barra ("unavailable" quando ele falhou). */
  route?: string | null;
  /** Resposta analítica da Central (rota central_question): números verificados, limites e links. */
  central?: CentralQuestionAnswer | null;
}

/** Número citado na resposta da Central: a chave do contexto e o valor exato exibido. */
export interface CentralCitation {
  key: string;
  label: string;
  display: string;
}

export interface CentralQuestionAnswer {
  /** answered | insufficient_data | out_of_scope | unavailable */
  status: string;
  refused: boolean;
  answer: string;
  limits: string[];
  missing: string[];
  citations: CentralCitation[];
  links: { key: string; title: string; path: string }[];
  periodKey: string;
  periodLabel: string;
  sectors: { id: number; name: string }[];
  generatedAt: string;
}

export interface AssistResult {
  text: string;
  usedLlm: boolean;
  source?: string | null;
}

export interface MemoryFieldDto {
  slot: string;
  label: string;
  value: string;
  basis: string;
  status: string;
  updatedAt?: string;
}

export interface MemoryDto {
  contactId: number;
  summary?: string | null;
  facts: string[];
  suggestion?: string | null;
  usedLlm: boolean;
  properties?: MemoryFieldDto[];
  history?: MemoryFieldDto[];
}

export interface RegisterResult {
  contactId?: number | null;
  contactName: string;
  organizedNotes: string;
  proposedUpdates: string[];
  memoryPreview?: string | null;
  usedLlm: boolean;
}

export interface ApprovalDto {
  id: number;
  sectorId: number;
  contactId?: number | null;
  contactName?: string | null;
  title: string;
  body: string;
  suggestedAction?: string | null;
  status: string;
  actionKind?: string | null;
  actionPayload?: string | null;
  executionResult?: string | null;
  executed?: boolean | null;
  navigatePath?: string | null;
  executionError?: string | null;
  createdAt: string;
  expiresAt?: string | null;
  reversible?: boolean;
  /** Código estável do motivo do status (rejeição, supersedência, shadow...). */
  statusReason?: string | null;
  /** Pergunta única da IA ao vendedor quando status = needs_info. */
  clarificationQuestion?: string | null;
  /** Slots faltantes (códigos estáveis) quando status = needs_info. */
  missingSlots?: string[] | null;
  approvedBy?: number | null;
  approvedAt?: string | null;
}

/** Lista fechada aceita pelo backend (MendsAiApprovalLifecycle.RejectReasonCodes); outro valor => HTTP 400. */
export type RejectReasonCode = 'already_talked' | 'bad_timing' | 'bad_text' | 'other';

export interface OpportunityDto {
  contactId: number;
  contactName: string;
  stage: string;
  reason: string;
  lastMessage?: string | null;
  lastMessageTime?: string | null;
  aiActive: number;
  path: string;
}

export interface BriefingTopic {
  kind: string;
  title: string;
  items: string[];
}

export interface BriefingDto {
  text: string;
  usedLlm: boolean;
  generatedAt: string;
  highlights: string[];
  topics?: BriefingTopic[];
}

export interface TranscribeResult {
  text: string;
  usedWhisper: boolean;
  durationMs: number;
}

export interface ChatSessionDto {
  id: string;
  sectorId: number;
  title: string;
  messagesJson: string;
  updatedAt: string;
  createdAt: string;
}

const base = `${API_URL}/mends-vision`;

export async function fetchToday(sectorId: number): Promise<TodayDto> {
  return unwrap(axios.get(`${base}/today`, { params: { sectorId }, headers: authHeaders() }));
}

export async function searchVision(sectorId: number, q: string): Promise<SearchResult> {
  return unwrap(axios.get(`${base}/search`, { params: { sectorId, q }, headers: authHeaders() }));
}

export async function mindChat(payload: {
  sectorId: number;
  message: string;
  history?: { role: string; content: string }[];
  contactId?: number;
  openDraft?: MindChatOpenDraft;
  /** Pergunta feita a partir da Central: mesmas conexões e período da tela. */
  centralSectorIds?: number[];
  centralPeriod?: CentralPeriodKey;
  source?: 'central' | 'palette' | 'mind';
}): Promise<MindChatResult> {
  return unwrap(axios.post(`${base}/mind-chat`, payload, { headers: authHeaders() }));
}

export async function summarizeConversation(sectorId: number, contactId: number): Promise<AssistResult> {
  return unwrap(axios.post(`${base}/summarize`, { sectorId, contactId }, { headers: authHeaders() }));
}

export async function suggestReply(sectorId: number, contactId: number): Promise<AssistResult> {
  return unwrap(axios.post(`${base}/suggest-reply`, { sectorId, contactId }, { headers: authHeaders() }));
}

export async function getMemory(sectorId: number, contactId: number, signal?: AbortSignal): Promise<MemoryDto> {
  return unwrap(axios.get(`${base}/memory/${contactId}`, { params: { sectorId }, headers: authHeaders(), signal }));
}

export async function refreshMemory(sectorId: number, contactId: number): Promise<MemoryDto> {
  return unwrap(axios.post(`${base}/memory/${contactId}/refresh`, null, { params: { sectorId }, headers: authHeaders() }));
}

export async function captureMemory(sectorId: number, contactId: number, text: string, source = 'form'): Promise<MemoryDto> {
  return unwrap(axios.post(`${base}/memory/${contactId}/capture`, { sectorId, text, source }, { headers: authHeaders() }));
}

export async function registerConversation(payload: {
  sectorId: number;
  contactId?: number | null;
  contactNameHint?: string;
  text: string;
}): Promise<RegisterResult> {
  return unwrap(axios.post(`${base}/register-conversation`, payload, { headers: authHeaders() }));
}

export async function createApproval(payload: {
  sectorId: number;
  contactId?: number | null;
  contactName?: string | null;
  title: string;
  body: string;
  suggestedAction?: string | null;
  actionKind?: string | null;
  actionPayload?: string | null;
  /** Origem do texto para a telemetria (CreateApprovalRequest.TextSource): user quando o vendedor editou. */
  textSource?: 'llm' | 'user';
  /** Mesma chave ⇒ mesma aprovação ativa (CreateApprovalRequest.IdempotencyKey, até 64 caracteres). */
  idempotencyKey?: string;
}): Promise<ApprovalDto> {
  return unwrap(axios.post(`${base}/approvals`, payload, { headers: authHeaders() }));
}

export async function listApprovals(sectorId: number): Promise<ApprovalDto[]> {
  return unwrap(axios.get(`${base}/approvals`, { params: { sectorId }, headers: authHeaders() }));
}

export async function approveItem(sectorId: number, id: number): Promise<ApprovalDto> {
  return unwrap(axios.post(`${base}/approvals/${id}/approve`, null, { params: { sectorId }, headers: authHeaders() }));
}

export async function editApproval(
  sectorId: number,
  id: number,
  body?: string,
  suggestedAction?: string,
  actionPayload?: string,
): Promise<ApprovalDto> {
  return unwrap(axios.post(
    `${base}/approvals/${id}/edit`,
    { body, suggestedAction, actionPayload },
    { params: { sectorId }, headers: authHeaders() },
  ));
}

/**
 * Responde à pergunta única da IA (status needs_info). Completa ⇒ volta como pending;
 * incompleta ⇒ cancelled (statusReason info_incomplete), sem nova pergunta.
 * HTTP 409 quando a aprovação não aguarda mais resposta (já respondida, expirada ou em outro status).
 */
export async function answerApproval(sectorId: number, id: number, answer: string): Promise<ApprovalDto> {
  return unwrap(axios.post(
    `${base}/approvals/${id}/answer`,
    { answer },
    { params: { sectorId }, headers: authHeaders() },
  ));
}

export async function undoApproval(sectorId: number, id: number): Promise<ApprovalDto> {
  return unwrap(axios.post(`${base}/approvals/${id}/undo`, null, { params: { sectorId }, headers: authHeaders() }));
}

export async function rejectApproval(
  sectorId: number,
  id: number,
  reason?: string,
  reasonCode?: RejectReasonCode,
): Promise<ApprovalDto> {
  return unwrap(axios.post(
    `${base}/approvals/${id}/reject`,
    { reason, reasonCode },
    { params: { sectorId }, headers: authHeaders() },
  ));
}

export async function fetchOpportunities(sectorId: number): Promise<OpportunityDto[]> {
  return unwrap(axios.get(`${base}/opportunities`, { params: { sectorId }, headers: authHeaders() }));
}

export async function generateBriefing(sectorId: number, llm = true): Promise<BriefingDto> {
  return unwrap(axios.post(`${base}/briefing?llm=${llm}`, { sectorId }, { headers: authHeaders() }));
}

export async function fetchFollowUpStatus(sectorId: number) {
  return unwrap(axios.get(`${base}/follow-up-status`, { params: { sectorId }, headers: authHeaders() }));
}

export async function transcribeAudio(
  sectorId: number,
  blob: Blob,
  fileName = 'voice.webm',
  durationMs?: number,
  /** Registrar: liga a transcrição ao trace do relato no servidor. */
  requestId?: string,
  /** 'recorded': o tempo real caiu e o usuário pediu para transcrever o áudio gravado (modelo de reserva). */
  mode?: 'recorded',
): Promise<TranscribeResult> {
  const form = new FormData();
  form.append('audio', blob, fileName);
  const token = SessionService.getToken();
  const res = await axios.post(
    `${base}/transcribe`,
    form,
    {
      params: { sectorId, durationMs, requestId, mode },
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  );
  const raw = (res.data.data ?? res.data.Data) as TranscribeResult;
  return normalizeKeys(raw) as TranscribeResult;
}

export async function listChatSessions(sectorId: number): Promise<ChatSessionDto[]> {
  return unwrap(axios.get(`${base}/chat-sessions`, { params: { sectorId }, headers: authHeaders() }));
}

export async function saveChatSession(payload: {
  sectorId: number;
  id?: string;
  title: string;
  messagesJson: string;
}): Promise<ChatSessionDto> {
  return unwrap(axios.post(`${base}/chat-sessions`, payload, { headers: authHeaders() }));
}

export async function deleteChatSession(sectorId: number, id: string): Promise<boolean> {
  return unwrap(axios.delete(`${base}/chat-sessions/${id}`, { params: { sectorId }, headers: authHeaders() }));
}

/** Hoje v2 — varredura server-side com cache. */
export interface TodayV2Dto {
  /** IB-05 — contatos com prazo de atendimento vencido e escalado (episódios abertos). */
  slaBreachedCount?: number;
  greeting: string;
  subtitle: string;
  actionsDoneToday: number;
  needsYou: TodayItem[];
  urgentConversations: TodayItem[];
  /** IA ativa com atividade nas últimas 72h — não é urgência (HJ-02/HJ-03). */
  aiAttending?: TodayItem[];
  /** Total real de aiAttending (a lista é cortada). */
  aiAttendingCount?: number;
  /** Cliente falou por último (72h) sem resposta — total real. */
  awaitingReplyCount?: number;
  /** Subconjunto de awaitingReplyCount com IA pausada. */
  needsHumanCount?: number;
  /** Conversas paradas entre 4 e 30 dias — total real. */
  riskSilentCount?: number;
  agenda: TodayItem[];
  followUps: TodayItem[];
  risks: TodayItem[];
  whileYouWereAway: TodayItem[];
  bestNextActions: TodayItem[];
  approvalsPending: number;
  /** HJ-05 — itens da agenda que começam hoje (fuso da agenda); mesmo número do KPI. */
  agendaTodayCount?: number;
  /** HJ-05 — próximo item da agenda que ainda vai começar. */
  agendaNextAt?: string | null;
  /** Fuso da agenda (calendar_settings) ou America/Sao_Paulo. */
  timezone?: string;
  /** Card "com problema" (últimas 24h, não resolvidos): IA sem resposta, não entregue, envio falho, funcionário com erro. */
  problems?: TodayItem[];
  problemCounts?: Record<string, number>;
  problemsCount?: number;
  /** HJ-06 — o que a IA executou nas últimas 24h (lista cortada; total real em aiActionsCount). */
  aiActions?: TodayItem[];
  aiActionsCount?: number;
  /** Total por fonte (crm_move, meeting, follow_up, commitment, audit, approval). */
  aiActionCounts?: Record<string, number>;
  /** Seções que não carregaram nesta abertura (banco fora, pool cheio). Vazio = tudo carregou. */
  degraded?: string[];
  /** Tempo por fase (ms). */
  timings?: Record<string, number>;
  usedLlm: boolean;
  fromCache?: boolean;
}

export interface TodayAiActionsDto {
  items: TodayItem[];
  total: number;
  windowHours: number;
  generatedAt: string;
}

/** HJ-06 — lista completa do que a IA executou nas últimas 24h. */
export async function fetchTodayAiActions(sectorId: number): Promise<TodayAiActionsDto> {
  const d = await unwrap(axios.get(`${base}/today-v2/ai-actions`, { params: { sectorId }, headers: authHeaders() })) as TodayAiActionsDto;
  return { ...d, items: d.items || [], total: d.total ?? (d.items || []).length };
}

export interface RecommendationDto {
  id: string;
  kind: string;
  title: string;
  what: string;
  why: string;
  evidence: string[];
  affectedObjects: Array<{
    type: string;
    id?: string | number | null;
    label: string;
    path?: string | null;
  }>;
  proposedAction?: string | null;
  actionKind?: string | null;
  actionPayload?: string | null;
  actionLabel?: string | null;
  autonomyStatus: string;
  priority: string;
  contactId?: number | null;
  contactName?: string | null;
}

export interface RecommendationsDto {
  items: RecommendationDto[];
  usedLlm: boolean;
  generatedAt: string;
}

export async function fetchTodayV2(sectorId: number, refresh = false): Promise<TodayV2Dto> {
  const d = await unwrap(axios.get(`${base}/today-v2`, { params: { sectorId, refresh }, headers: authHeaders() })) as TodayV2Dto;
  return {
    ...d,
    greeting: d.greeting || 'Hoje',
    subtitle: d.subtitle || '',
    actionsDoneToday: d.actionsDoneToday ?? 0,
    needsYou: d.needsYou || [],
    urgentConversations: d.urgentConversations || [],
    agenda: d.agenda || [],
    followUps: d.followUps || [],
    risks: d.risks || [],
    whileYouWereAway: d.whileYouWereAway || [],
    bestNextActions: d.bestNextActions || [],
    approvalsPending: d.approvalsPending ?? 0,
    problems: d.problems || [],
    problemsCount: d.problemsCount ?? (d.problems || []).length,
    aiActions: d.aiActions || [],
    aiActionsCount: d.aiActionsCount ?? (d.aiActions || []).length,
    usedLlm: !!d.usedLlm,
  };
}

export interface ProposedAction {
  id: string;
  actionKind: string;
  title: string;
  detail: string;
  evidence?: string | null;
  actionPayload: string;
}

export interface RegisterPlan {
  contactId?: number | null;
  contactName: string;
  contactNameGuess?: string | null;
  isQuestion: boolean;
  answer?: string | null;
  summary: string;
  memoryUpdates: string[];
  actions: ProposedAction[];
  usedLlm: boolean;
  rawTranscript: string;
  /** REG-09 — relato parece falar de outra pessoa que o contato da tela. */
  contactConflict?: boolean;
  conflictCandidates?: Array<{
    id: number; name: string; phoneTail?: string | null; column?: string | null;
    lastMessage?: string | null; lastMessageAt?: string | null;
  }>;
  /** REG-08 — nome/telefone citado casa com mais de um contato; candidatos em conflictCandidates. */
  contactAmbiguous?: boolean;
  /** O nome citado não existe entre os contatos do setor (nada é vinculado nem enviado). */
  contactNotFound?: boolean;
  /** Tipo da interação externa relatada (ligacao, reuniao, visita…). */
  interactionType?: string | null;
  requestId?: string | null;
  attachments?: RegisterAttachment[];
}

export interface RegisterAttachmentFact {
  fact: string;
  /** "foto: …" ou "documento: …" — o que o leitor viu no arquivo. */
  evidence: string;
}

/** Foto/PDF lido pelo modelo multimodal do gateway (o arquivo em si não é guardado). */
export interface RegisterAttachment {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  fileKind: 'image' | 'pdf' | string;
  readable: boolean;
  documentType?: string | null;
  summary: string;
  facts: RegisterAttachmentFact[];
  unreadableReason?: string | null;
}

export type RegisterInputType = 'text' | 'voice' | 'file' | 'mixed';

export async function interpretRegisterAttachment(
  sectorId: number,
  file: File,
  opts: { contactId?: number | null; requestId?: string; note?: string } = {},
): Promise<RegisterAttachment> {
  const form = new FormData();
  form.append('file', file, file.name);
  if (opts.note) form.append('note', opts.note);
  const res = await axios.post(`${base}/register-conversation/attachment`, form, {
    params: { sectorId, contactId: opts.contactId || undefined, requestId: opts.requestId },
    headers: authHeaders(),
  });
  return normalizeKeys(res.data.data ?? res.data.Data) as RegisterAttachment;
}

export async function analyzeRegisterConversation(payload: {
  sectorId: number;
  contactId?: number | null;
  contactNameHint?: string;
  text: string;
  screenContext?: RegisterScreenContextPayload;
  requestId?: string;
  inputType?: RegisterInputType;
  sourceSurface?: string;
  conversationId?: number | null;
  opportunityId?: string | null;
  attachments?: RegisterAttachment[];
}): Promise<RegisterPlan> {
  return unwrap(axios.post(`${base}/register-conversation/analyze`, payload, { headers: authHeaders() }));
}

/** Destino de cada ação confirmada no Registrar (nada some em silêncio). */
export interface RegisterConfirmItem {
  actionId: string;
  actionKind: string;
  /** queued | needs_info | existing_needs_info | executed | suppressed | skipped */
  outcome: string;
  /** Código estável do motivo (missing:date, needs_info_open, shadow, unknown_kind…). */
  reasonCode?: string | null;
  approvalId?: number | null;
  /** Resultado real do executor quando executou (ex.: "Follow-up agendado para 12/10 15:00 — João"). */
  result?: string | null;
  /** Título legível (também para itens só do servidor, como a nota do relato). */
  title?: string | null;
  evidence?: string | null;
  /** Prévia da confirmação única (mensagem exata, destinatário, canal, horário) ou do comando. */
  preview?: RegisterSendPreview | null;
  /** Alvo ambíguo: candidatos com o que distingue cada um. */
  candidates?: RegisterLeadCandidate[] | null;
  canUndo?: boolean;
  contactId?: number | null;
  contactName?: string | null;
}

export interface RegisterSendPreview {
  /** message | command | bulk (comando em lote, F4) | preference (preferência dita em frase, F4) | email (e-mail avulso) */
  kind: 'message' | 'command' | 'bulk' | 'preference' | 'email' | string;
  /** E-mail: assunto, destinatário (e-mail) e caixa de onde sai. */
  subject?: string | null;
  emailTo?: string | null;
  emailFrom?: string | null;
  subjectDrafted?: boolean;
  text?: string | null;
  contactId?: number | null;
  contactName?: string | null;
  phoneTail?: string | null;
  channel: string;
  whenIso?: string | null;
  whenLabel?: string | null;
  drafted?: boolean;
  summary?: string | null;
  warnings: string[];
  blocked?: boolean;
  blockedReason?: string | null;
  /** F4: critério, contagem, lista e resultado do comando em lote. */
  bulk?: import('./aiCompanyService').RegisterBulkPreview | null;
  /** F4: "entendi assim" da preferência dita em frase. */
  preference?: import('./aiCompanyService').PreferenceDraft | null;
}

export interface RegisterLeadCandidate {
  id: number;
  name: string;
  phoneTail?: string | null;
  email?: string | null;
  column?: string | null;
  lastMessage?: string | null;
  lastMessageAt?: string | null;
}

/** O único clique: envia/agenda a mensagem (com o texto editado), executa o comando ou aplica o cliente escolhido. */
export async function confirmRegisterAction(payload: {
  sectorId: number; requestId: string; actionId: string; text?: string; subject?: string; contactId?: number;
}): Promise<RegisterConfirmItem> {
  return unwrap(axios.post(`${base}/register-conversation/action/confirm`, payload, { headers: authHeaders() }));
}

/** Resposta ao esclarecimento de um item em needs_info: o servidor reinterpreta o pedido com ela e executa (ou pergunta de novo). */
export async function answerRegisterAction(payload: {
  sectorId: number; requestId: string; actionId: string; answer: string;
}): Promise<RegisterConfirmItem> {
  return unwrap(axios.post(`${base}/register-conversation/action/answer`, payload, { headers: authHeaders() }));
}

export async function cancelRegisterAction(payload: { sectorId: number; requestId: string; actionId: string }): Promise<RegisterConfirmItem> {
  return unwrap(axios.post(`${base}/register-conversation/action/cancel`, payload, { headers: authHeaders() }));
}

export async function undoRegisterAction(payload: { sectorId: number; requestId: string; actionId: string }): Promise<RegisterConfirmItem> {
  return unwrap(axios.post(`${base}/register-conversation/action/undo`, payload, { headers: authHeaders() }));
}

/**
 * Sem contato resolvido e com ação que precisa dele, o backend responde HTTP 400 com
 * code CONTACT_REQUIRED e `candidates: { id, name }[]` para o vendedor escolher.
 */
export async function confirmRegisterConversation(payload: {
  sectorId: number;
  contactId?: number | null;
  contactName?: string;
  summary?: string;
  rawTranscript: string;
  actions: ProposedAction[];
  screenContext?: RegisterScreenContextPayload;
  /** REG-39 — mesmo id = mesmo relato: retry/duplo clique não duplica nada. */
  requestId?: string;
  inputType?: RegisterInputType;
  sourceSurface?: string;
  conversationId?: number | null;
  opportunityId?: string | null;
  interactionType?: string | null;
  attachments?: RegisterAttachment[];
}): Promise<{
  approvalsCreated: number;
  approvalIds: number[];
  memorySaved: boolean;
  contactId?: number | null;
  contactName?: string | null;
  items?: RegisterConfirmItem[];
  logId?: number | null;
  requestId?: string | null;
}> {
  return unwrap(axios.post(`${base}/register-conversation/confirm`, payload, { headers: authHeaders() }));
}

/** Itens do confirm em andamento que já terminaram (progresso por item; vazio quando nada roda). */
export async function fetchRegisterProgress(sectorId: number, requestId: string): Promise<RegisterConfirmItem[]> {
  const data = await unwrap(axios.get(`${base}/register-conversation/progress`, {
    params: { sectorId, requestId },
    headers: authHeaders(),
  })) as { items?: RegisterConfirmItem[] };
  return data?.items || [];
}

/** Item do relato como o servidor guarda, com o destino atual da aprovação ligada. */
export interface RegisterLogItem {
  actionId: string;
  actionKind: string;
  title?: string | null;
  detail?: string | null;
  evidence?: string | null;
  outcome: string;
  reasonCode?: string | null;
  approvalId?: number | null;
  result?: string | null;
  approvalStatus?: string | null;
  /** Destino atual: executed quando a aprovação foi aprovada, undone quando desfeita… */
  currentOutcome: string;
  currentResult?: string | null;
  preview?: RegisterSendPreview | null;
  candidates?: RegisterLeadCandidate[] | null;
  canUndo?: boolean;
  contactId?: number | null;
  contactName?: string | null;
}

/** Relato do Registrar no servidor — o mesmo para qualquer usuário/dispositivo do setor (REGX-05). */
export interface RegisterLogEntry {
  id: number;
  sectorId: number;
  requestId: string;
  status: 'draft' | 'awaiting_clarification' | 'confirmed' | 'failed' | string;
  inputType: RegisterInputType | string;
  rawTranscript: string;
  summary?: string | null;
  interactionType?: string | null;
  contactId?: number | null;
  contactName?: string | null;
  conversationId?: number | null;
  opportunityId?: string | null;
  sourceSurface?: string | null;
  authorUserId?: number | null;
  authorName?: string | null;
  attachments: RegisterAttachment[];
  items: RegisterLogItem[];
  candidates?: Array<{ id: number; name: string }> | null;
  approvalIds: number[];
  memorySaved: boolean;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
}

export async function listRegisterLogServer(
  sectorId: number,
  opts: { contactId?: number | null; limit?: number } = {},
): Promise<RegisterLogEntry[]> {
  const data = await unwrap(axios.get(`${base}/register-log`, {
    params: { sectorId, contactId: opts.contactId || undefined, limit: opts.limit ?? 40 },
    headers: authHeaders(),
  }));
  return Array.isArray(data) ? (data as RegisterLogEntry[]) : [];
}

/** O que chegou e foi aceito (ou está em revisão) como o documento esperado. */
export interface DocumentConditionEvidence {
  source: string;
  sourceId?: number | null;
  fileName?: string | null;
  documentType?: string | null;
  summary?: string | null;
  evidence?: string | null;
  confidence?: number | null;
  receivedAtUtc: string;
  reviewedByUserId?: number | null;
}

/** Documento que o cliente prometeu enviar e que o Registrar acompanha (WhatsApp e e-mail). */
export interface DocumentCondition {
  id: string;
  contactId: number;
  conditionText: string;
  documentLabel: string;
  action: string;
  state: string;
  stateLabel: string;
  dueAt?: string | null;
  expiresAt?: string | null;
  createdAt: string;
  evidence?: DocumentConditionEvidence | null;
  approvalId?: number | null;
  detail?: string | null;
  canReview: boolean;
  canCancel: boolean;
  checks: Array<{
    sourceKind: string;
    sourceId: number;
    verdict: string;
    confidence?: number | null;
    summary?: string | null;
    createdAt: string;
  }>;
}

export async function listDocumentConditions(
  sectorId: number,
  contactId: number,
  signal?: AbortSignal,
): Promise<DocumentCondition[]> {
  const data = await unwrap(axios.get(`${base}/document-conditions`, {
    params: { sectorId, contactId },
    headers: authHeaders(),
    signal,
  }));
  return Array.isArray(data) ? (data as DocumentCondition[]) : [];
}

/** "É este documento" (accept) atende a condição e prepara a confirmação; "Não é" volta a aguardar. */
export async function reviewDocumentCondition(sectorId: number, id: string, accept: boolean): Promise<DocumentCondition> {
  return unwrap(axios.post(`${base}/document-conditions/${id}/review`, { sectorId, accept }, { headers: authHeaders() }));
}

export async function cancelDocumentCondition(sectorId: number, id: string): Promise<DocumentCondition> {
  return unwrap(axios.post(`${base}/document-conditions/${id}/cancel`, { sectorId }, { headers: authHeaders() }));
}

export interface RegisterPolicy {
  sectorId: number;
  maxAutoDiscountPercent?: number | null;
  defaultFollowUpTime?: string | null;
}

export async function fetchRegisterPolicy(sectorId: number): Promise<RegisterPolicy> {
  return unwrap(axios.get(`${base}/register-policy`, { params: { sectorId }, headers: authHeaders() }));
}

/** Card do funil único: etapa = coluna do Kanban; evidência = última movimentação para a coluna. */
export interface FunnelItem {
  contactId: number;
  contactName: string;
  /** Nome da coluna. */
  stage: string;
  columnId: number;
  /** entrada | etapa | ganho | perdido; null = etapa comum. */
  kind?: string | null;
  temperature?: string | null;
  evidence?: string | null;
  movedBy?: string | null;
  moveConfidence?: string | null;
  movedAt?: string | null;
  estimatedValue?: number | null;
  /** USD/BRL/EUR quando a conversa cita a moeda; null = desconhecida. */
  currency?: string | null;
  silentDays?: number | null;
  path: string;
  isOpportunity?: boolean;
  agentId?: number | null;
  agentName?: string | null;
  agentKind?: string | null;
}

export interface FunnelStage {
  /** Id da coluna como texto. */
  stage: string;
  /** Nome da coluna. */
  label: string;
  columnId: number;
  kind?: string | null;
  position: number;
  count: number;
  totalValue: number;
  valueBrl: number;
  items: FunnelItem[];
}

export interface FunnelDto {
  stages: FunnelStage[];
  noClearStage: FunnelItem[];
  contactsOnly?: FunnelItem[];
  usedLlm: boolean;
}

export async function fetchFunnel(sectorId: number, infer = false): Promise<FunnelDto> {
  return unwrap(axios.get(`${base}/funnel`, { params: { sectorId, infer }, headers: authHeaders() }));
}

export interface FunnelStageSnapshot {
  contactId: number;
  stage: string;
  label: string;
  columnId: number;
  kind?: string | null;
  evidence?: string | null;
  movedBy?: string | null;
  moveConfidence?: string | null;
  movedAt?: string | null;
  temperature?: string | null;
  isOpportunity: boolean;
}

/** Etapa (coluna do Kanban) e última movimentação de cada contato. */
export async function fetchFunnelStages(sectorId: number, contactIds: number[]): Promise<FunnelStageSnapshot[]> {
  if (!contactIds.length) return [];
  const data = await unwrap(axios.get(`${base}/funnel/stages`, {
    params: { sectorId, contactIds: contactIds.join(',') },
    headers: authHeaders(),
  }));
  return Array.isArray(data) ? data : [];
}

export interface DirectorDecision {
  title: string;
  why: string;
  priority: string;
  actionKind?: string | null;
  actionPayload?: string | null;
  actionLabel?: string | null;
  contactId?: number | null;
  contactName?: string | null;
}

export interface DirectorMetric {
  value: string;
  label: string;
  why: string;
}

export interface DirectorBriefingDto {
  briefing: string;
  decisions: DirectorDecision[];
  workNow: string[];
  metrics: DirectorMetric[];
  usedLlm: boolean;
  generatedAt: string;
}

export async function fetchDirectorBriefing(sectorId: number, regenerate = false): Promise<DirectorBriefingDto> {
  return unwrap(axios.get(`${base}/director-briefing`, { params: { sectorId, regenerate }, headers: authHeaders() }));
}

/**
 * `seed`: varredura automática ao abrir uma tela (orçamento curto no backend: 20 s, até 2 itens).
 * `manual`: o vendedor pediu (botão "Propor follow-ups"; 60 s).
 */
export type FollowUpSweepTrigger = 'seed' | 'manual';

export async function proposeFollowUps(
  sectorId: number,
  max = 5,
  trigger: FollowUpSweepTrigger = 'manual',
): Promise<{
  candidatesFound: number;
  approvalsCreated: number;
}> {
  return unwrap(axios.post(`${base}/follow-up-sweep`, null, { params: { sectorId, max, trigger }, headers: authHeaders() }));
}

export interface AutonomyEntry {
  actionKind: string;
  level: string;
}

export async function fetchAutonomy(sectorId: number): Promise<AutonomyEntry[]> {
  return unwrap(axios.get(`${base}/autonomy`, { params: { sectorId }, headers: authHeaders() }));
}

export async function setAutonomy(sectorId: number, actionKind: string, level: string): Promise<AutonomyEntry> {
  return unwrap(axios.post(`${base}/autonomy`, { sectorId, actionKind, level }, { headers: authHeaders() }));
}

export interface RegisterCommandEffect {
  tool: string;
  ok: boolean;
  summary: string;
}

export interface RegisterCommandResult {
  reply: string;
  executed: RegisterCommandEffect[];
  navigateTo?: string | null;
  usedLlm: boolean;
}

export async function runRegisterCommand(payload: {
  sectorId: number;
  contactId?: number | null;
  contactNameHint?: string;
  text: string;
  history?: { role: string; content: string }[];
  screenContext?: RegisterScreenContextPayload;
}): Promise<RegisterCommandResult> {
  return unwrap(axios.post(`${base}/register-command`, payload, { headers: authHeaders() }));
}

export async function fetchRecommendations(sectorId: number, refresh = false): Promise<RecommendationsDto> {
  const d = await unwrap(axios.get(`${base}/recommendations`, { params: { sectorId, refresh }, headers: authHeaders() })) as RecommendationsDto;
  return {
    ...d,
    items: (d.items || []).map((item) => ({
      ...item,
      evidence: item.evidence || [],
      affectedObjects: item.affectedObjects || [],
    })),
    usedLlm: !!d.usedLlm,
    generatedAt: d.generatedAt || new Date().toISOString(),
  };
}

// ==== Central de Inteligência (visão de gestão: GET api/mends-vision/central/*) ====

export type CentralPeriodKey = 'today' | '7d' | '14d' | '30d';

export interface CentralPeriod {
  key: CentralPeriodKey;
  label: string;
  timezone: string;
  from: string;
  to: string;
  previousFrom: string;
  previousTo: string;
  comparisonLabel: string;
}

export interface CentralKpi {
  key: string;
  label: string;
  value: number | null;
  previous: number | null;
  deltaPct: number | null;
  unit: 'count' | 'minutes' | 'percent' | 'currency';
  /** Moeda ISO quando unit = currency (só existe quando há valor registrado). */
  currency?: string | null;
  higherIsBetter: boolean;
  available: boolean;
  unavailableReason?: string | null;
  howCalculated: string;
  hint?: string | null;
  path?: string | null;
  /** Base da comparação quando não é o período anterior do filtro (ex.: "vs. setembro de 2026 até o mesmo dia"). */
  comparisonLabel?: string | null;
  /** Retrato de agora (não depende do período e não tem comparação). */
  snapshot?: boolean;
}

export interface CentralSectorPerformance {
  sectorId: number;
  sectorName: string;
  conversationsAi: number;
  previousConversationsAi: number;
  conversationsActive: number;
  aiMessages: number;
  humanMessages: number;
  inboundMessages: number;
  newContacts: number;
  meetingsBookedByAi: number;
  transfers: number;
  medianFirstResponseMinutes: number | null;
  unanswered: number;
  agentsActive: number;
  agentsTotal: number;
}

export interface CentralAgentPerformance {
  agentId: number;
  name: string;
  sectorId: number;
  sectorName: string;
  enabled: boolean;
  roles: string[];
  conversations: number;
  previousConversations: number;
  turns: number;
  emptyTurns: number;
  meetingsBooked: number;
  transfers: number;
  latencyP50Seconds: number | null;
  latencyP90Seconds: number | null;
  lastActivityAt: string | null;
  crmEnabled: boolean;
  followUpEnabled: boolean;
  health: 'ok' | 'attention' | 'idle' | 'paused';
  healthLabel: string;
  healthNotes: string[];
  editPath: string;
}

export interface CentralBreakdownItem {
  key: string;
  label: string;
  count: number;
  hint?: string | null;
}

export interface CentralChannel {
  key: string;
  label: string;
  status: 'connected' | 'not_connected';
  conversations?: number | null;
  messages?: number | null;
  /** Motivo quando não está conectado (ex.: a Meta recusou o acesso) ou aviso em um canal conectado. */
  detail?: string | null;
}

export interface CentralDailyPoint {
  date: string;
  inbound: number;
  aiMessages: number;
  humanMessages: number;
  meetingsBooked: number;
}

export interface CentralCase {
  contactId: number;
  contactName: string;
  sectorId: number;
  sectorName: string;
  at: string | null;
  evidence: string;
  value?: string | null;
  owner?: string | null;
  path: string;
  secondaryLabel?: string | null;
  secondaryPath?: string | null;
}

export interface CentralFinding {
  id: string;
  tone: 'risk' | 'opportunity' | 'attention';
  title: string;
  count: number;
  what: string;
  why: string;
  limits: string[];
  howCalculated: string;
  severity: 'high' | 'normal' | 'low';
  scope: 'period' | 'now';
  cases: CentralCase[];
  listPath?: string | null;
}

export interface CentralActionTarget {
  sectorId: number;
  contactId: number;
  contactName: string;
  idempotencyKey: string;
}

export interface CentralActionStatus {
  pending: number;
  executed: number;
  rejected: number;
  other: number;
  total: number;
}

/** Passo de um plano da Central: só `message` fala com o cliente (vira aprovação); os outros são internos. */
export type CentralPlanStepKind = 'note' | 'move' | 'tag' | 'message';

export interface CentralPlanTarget {
  sectorId: number;
  contactId: number;
  contactName: string;
  cardId?: number | null;
  detail: string;
  columnId?: number | null;
  tagId?: number | null;
  idempotencyKey?: string | null;
}

export interface CentralPlanStep {
  id: string;
  kind: CentralPlanStepKind;
  label: string;
  what: string;
  talksToClient: boolean;
  targets: CentralPlanTarget[];
}

export type CentralPlanTargetStatus = 'done' | 'queued' | 'executed' | 'skipped' | 'failed';

export interface CentralPlanRun {
  key: string;
  recommendationId: string;
  alreadyRan: boolean;
  executedAt: string;
  steps: Array<{
    stepId: string;
    kind: CentralPlanStepKind;
    label: string;
    talksToClient: boolean;
    results: Array<{
      sectorId: number;
      contactId: number;
      contactName: string;
      status: CentralPlanTargetStatus;
      message: string;
      approvalId?: number | null;
    }>;
  }>;
  summary: string;
}

export interface CentralPlan {
  /** Idempotência central:plan:{recomendação}:{dia}. */
  key: string;
  steps: CentralPlanStep[];
  /** Execução já feita hoje (repetir devolve esta). */
  lastRun?: CentralPlanRun | null;
}

export interface CentralRecommendation {
  id: string;
  findingId: string;
  what: string;
  why: string;
  action: string;
  actionLabel: string;
  actionKind: 'follow_up_batch' | 'follow_up_sweep' | 'open' | 'plan';
  targets: CentralActionTarget[];
  path?: string | null;
  requiresApproval: boolean;
  status: CentralActionStatus;
  /** "Executar plano": prévia com os alvos e o que cada passo faz. */
  plan?: CentralPlan | null;
}

export interface CentralOverview {
  period: CentralPeriod;
  sectors: Array<{ id: number; name: string }>;
  kpis: CentralKpi[];
  sectorPerformance: CentralSectorPerformance[];
  agents: CentralAgentPerformance[];
  origins: CentralBreakdownItem[];
  channels: CentralChannel[];
  daily: CentralDailyPoint[];
  findings: CentralFinding[];
  recommendations: CentralRecommendation[];
  limits: string[];
  generatedAt: string;
  /** Radar de Receita (cards): valor em negociação, ponderado, paradas, sem valor e fechado no mês. */
  revenue?: RevenueRadar | null;
}

export interface CentralAnalysis {
  available: boolean;
  unavailableReason?: string | null;
  summary: string;
  priorities: Array<{ findingId: string; title: string; reason: string }>;
  limits: string[];
  generatedAt: string;
  fromCache?: boolean;
}

const emptyStatus: CentralActionStatus = { pending: 0, executed: 0, rejected: 0, other: 0, total: 0 };

/** Normaliza listas ausentes (API antiga ou vazia) para a tela nunca quebrar em `.map`. */
export function normalizeCentralOverview(d: Partial<CentralOverview>): CentralOverview {
  return {
    period: d.period as CentralPeriod,
    sectors: d.sectors || [],
    kpis: d.kpis || [],
    sectorPerformance: d.sectorPerformance || [],
    agents: (d.agents || []).map((a) => ({ ...a, roles: a.roles || [], healthNotes: a.healthNotes || [] })),
    origins: d.origins || [],
    channels: d.channels || [],
    daily: d.daily || [],
    findings: (d.findings || []).map((f) => ({ ...f, limits: f.limits || [], cases: f.cases || [] })),
    recommendations: (d.recommendations || []).map((r) => ({
      ...r,
      targets: r.targets || [],
      status: r.status || emptyStatus,
      plan: r.plan
        ? { ...r.plan, steps: (r.plan.steps || []).map((st) => ({ ...st, targets: st.targets || [] })) }
        : null,
    })),
    limits: d.limits || [],
    generatedAt: d.generatedAt || new Date().toISOString(),
    revenue: d.revenue ?? null,
  };
}

/**
 * Executa o plano de uma recomendação no servidor: passos internos na hora, os que falam com o cliente viram aprovação
 * (mesma criação do botão de aprovação). Repetir no mesmo dia devolve a mesma execução (alreadyRan).
 */
export async function executeCentralPlan(
  sectorIds: number[],
  period: CentralPeriodKey,
  recommendationId: string,
): Promise<CentralPlanRun> {
  const d = await unwrap(axios.post(`${base}/central/plan/execute`, { sectorIds, period, recommendationId }, {
    headers: authHeaders(),
  })) as CentralPlanRun;
  return { ...d, steps: (d.steps || []).map((s) => ({ ...s, results: s.results || [] })) };
}

export async function fetchCentralOverview(
  sectorIds: number[],
  period: CentralPeriodKey,
  refresh = false,
): Promise<CentralOverview> {
  const d = await unwrap(axios.get(`${base}/central/overview`, {
    params: { sectorIds: sectorIds.join(','), period, refresh },
    headers: authHeaders(),
  })) as Partial<CentralOverview>;
  return normalizeCentralOverview(d);
}

export async function fetchCentralAnalysis(
  sectorIds: number[],
  period: CentralPeriodKey,
  refresh = false,
): Promise<CentralAnalysis> {
  const d = await unwrap(axios.get(`${base}/central/analysis`, {
    params: { sectorIds: sectorIds.join(','), period, refresh },
    headers: authHeaders(),
  })) as Partial<CentralAnalysis>;
  return {
    available: !!d.available,
    unavailableReason: d.unavailableReason ?? null,
    summary: d.summary || '',
    priorities: d.priorities || [],
    limits: d.limits || [],
    generatedAt: d.generatedAt || new Date().toISOString(),
    fromCache: !!d.fromCache,
  };
}

export interface CentralTopicExample {
  quote: string;
  contactId: number;
  contactName: string;
  sectorId: number;
  date: string;
  path: string;
}

export interface CentralTopicCount {
  key: string;
  label: string;
  description: string;
  contacts: number;
  conversations: number;
  examples: CentralTopicExample[];
}

export interface CentralTopics {
  available: boolean;
  unavailableReason?: string | null;
  periodKey: CentralPeriodKey;
  periodLabel: string;
  taxonomyVersion: string;
  total: number;
  classified: number;
  pending: number;
  failed: number;
  noTopic: number;
  contactsClassified: number;
  complete: boolean;
  topics: CentralTopicCount[];
  howCalculated: string;
  generatedAt: string;
}

/** Top assuntos (IA sobre lista fixa, cache por conversa). Cada chamada classifica um lote do que falta. */
export async function fetchCentralTopics(
  sectorIds: number[],
  period: CentralPeriodKey,
  classify = true,
): Promise<CentralTopics> {
  const d = await unwrap(axios.get(`${base}/central/topics`, {
    params: { sectorIds: sectorIds.join(','), period, classify },
    headers: authHeaders(),
  })) as Partial<CentralTopics>;
  return {
    available: !!d.available,
    unavailableReason: d.unavailableReason ?? null,
    periodKey: (d.periodKey as CentralPeriodKey) || period,
    periodLabel: d.periodLabel || '',
    taxonomyVersion: d.taxonomyVersion || '',
    total: d.total ?? 0,
    classified: d.classified ?? 0,
    pending: d.pending ?? 0,
    failed: d.failed ?? 0,
    noTopic: d.noTopic ?? 0,
    contactsClassified: d.contactsClassified ?? 0,
    complete: !!d.complete,
    topics: Array.isArray(d.topics) ? d.topics : [],
    howCalculated: d.howCalculated || '',
    generatedAt: d.generatedAt || new Date().toISOString(),
  };
}

export interface MeetingPrepPoint {
  topic: string;
  text: string;
  evidence: string;
  source: 'conversa' | 'memoria' | 'oportunidade' | 'agenda' | string;
}

export interface MeetingPrep {
  ok: boolean;
  message?: string | null;
  eventId?: number | null;
  contactId?: number | null;
  contactName?: string | null;
  eventTitle?: string | null;
  startsAt?: string | null;
  headline: string;
  points: MeetingPrepPoint[];
  unknowns: string[];
  questions: string[];
  droppedUngrounded: number;
}

/** "Preparar reunião": resumo fundamentado do cliente (cada ponto com o trecho conferido). */
export async function fetchMeetingPrep(
  sectorId: number,
  target: { eventId?: number | null; contactId?: number | null },
): Promise<MeetingPrep> {
  return unwrap(axios.get(`${base}/meeting-prep`, {
    params: { sectorId, eventId: target.eventId ?? undefined, contactId: target.contactId ?? undefined },
    headers: authHeaders(),
  }));
}

export interface AgentPerformance {
  agentId: number;
  proposed: number;
  approvedAsIs: number;
  approvedEdited: number;
  rejected: number;
  expired: number;
  pending: number;
  messagesApproved: number;
  messagesReplied: number;
  approvalRate: number | null;
  replyRate: number | null;
}

export interface AgentPerformanceResult {
  period: string;
  fromUtc: string;
  agents: AgentPerformance[];
  howCalculated: string;
}

/** Funcionários: o que cada funcionário digital propôs e o que a equipe fez com isso. */
export async function fetchAgentPerformance(sectorId: number, period = '30d'): Promise<AgentPerformanceResult> {
  return unwrap(axios.get(`${base}/agents/performance`, { params: { sectorId, period }, headers: authHeaders() }));
}
